import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type SystemSettings as Settings } from '../api';
import { Alert, Badge, Button, Card, Flash, PageHeader, Skeleton } from '../components/ui';
import { TextField } from '../components/form';

/**
 * How the system behaves (SPEC §8 item 18).
 *
 * Deliberately a different screen from Form settings, which edits what the
 * *form* offers. These are two different jobs with two different blast radii:
 * getting a department wrong makes a dropdown untidy, getting the sending
 * identity wrong stops mail.
 *
 * Anything the server's configuration owns is shown here read-only rather than
 * left off. An administrator asking "why is no mail going out" should be able
 * to see that mail is switched off without a shell on the box, and equally
 * should not be handed a text box that pretends they can change it.
 */

type Draft = {
  mailFromName: string;
  mailReplyTo: string;
  nudgeIntervalHours: string;
  pdfHeaderText: string;
  pdfFooterText: string;
  retentionMonths: string;
  onsiteTechEmail: string;
};

const toDraft = (settings: Settings): Draft => ({
  onsiteTechEmail: settings.onsiteTechEmail ?? '',
  mailFromName: settings.mailFromName ?? '',
  mailReplyTo: settings.mailReplyTo ?? '',
  nudgeIntervalHours: String(settings.nudgeIntervalHours),
  pdfHeaderText: settings.pdfHeaderText ?? '',
  pdfFooterText: settings.pdfFooterText ?? '',
  retentionMonths: settings.retentionMonths === null ? '' : String(settings.retentionMonths),
});

export function SystemSettings() {
  const client = useQueryClient();
  const loaded = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seeded once the fetch lands. Held as strings because that is what inputs
  // are, and coercing on every keystroke makes a half-typed number vanish.
  useEffect(() => {
    if (loaded.data && draft === null) setDraft(toDraft(loaded.data.settings));
  }, [loaded.data, draft]);

  const save = useMutation({
    mutationFn: (next: Draft) =>
      api.saveSettings({
        mailFromName: next.mailFromName.trim() || null,
        mailReplyTo: next.mailReplyTo.trim() || null,
        nudgeIntervalHours: Number(next.nudgeIntervalHours),
        pdfHeaderText: next.pdfHeaderText.trim() || null,
        pdfFooterText: next.pdfFooterText.trim() || null,
        retentionMonths: next.retentionMonths.trim() === '' ? null : Number(next.retentionMonths),
        onsiteTechEmail: next.onsiteTechEmail.trim() || null,
      }),
    onSuccess: (result) => {
      setError(null);
      setDraft(toDraft(result.settings));
      void client.invalidateQueries({ queryKey: ['settings'] });
      setFlash(
        result.changed.length === 0
          ? 'Nothing had changed.'
          : `Saved ${result.changed.length} change${result.changed.length === 1 ? '' : 's'}.`,
      );
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save those settings.'),
  });

  if (loaded.isPending || draft === null) {
    return (
      <div>
        <PageHeader title="System settings" description="How this system behaves." />
        <Card className="space-y-4 p-6">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </Card>
      </div>
    );
  }
  if (loaded.isError) {
    return <Alert>{loaded.error instanceof ApiError ? loaded.error.message : 'Could not open settings.'}</Alert>;
  }

  const { settings, limits, environment } = loaded.data;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(settings));

  const hours = Number(draft.nudgeIntervalHours);
  const hoursError =
    draft.nudgeIntervalHours.trim() === '' || !Number.isInteger(hours)
      ? 'Whole hours, please.'
      : hours < limits.nudgeIntervalHours.min || hours > limits.nudgeIntervalHours.max
        ? `Between ${limits.nudgeIntervalHours.min} and ${limits.nudgeIntervalHours.max} hours.`
        : undefined;

  const months = Number(draft.retentionMonths);
  const monthsError =
    draft.retentionMonths.trim() === ''
      ? undefined
      : !Number.isInteger(months) || months < limits.retentionMonths.min || months > limits.retentionMonths.max
        ? `Between ${limits.retentionMonths.min} and ${limits.retentionMonths.max} months, or empty to keep records indefinitely.`
        : undefined;

  const blocked = Boolean(hoursError || monthsError);

  return (
    <div>
      <PageHeader
        title="System settings"
        description="How this system behaves. What the form itself offers is under Form settings."
        actions={
          settings.updatedBy ? (
            <Badge tone="neutral">
              Last changed by {settings.updatedBy},{' '}
              {new Date(settings.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
            </Badge>
          ) : undefined
        }
      />

      {flash && <Flash message={flash} onDismiss={() => setFlash(null)} />}
      {error && (
        <div className="mb-6">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="space-y-6">
        <Section
          title="Outgoing mail"
          description="Notifications and reminders are sent from a shared mailbox over Microsoft Graph."
        >
          <Fixed
            label="Sending address"
            value={environment.mailFromAddress ?? 'Not configured'}
            note="Set on the server. It has to match the Exchange access policy that confines this app to one mailbox, so changing it here would only produce mail Graph refuses to send."
          />
          <Fixed
            label="Mail"
            value={environment.mailEnabled ? 'Switched on' : 'Switched off: notifications are saved in the app only'}
            note="Set on the server, so that a test deployment cannot mail real people by accident."
          />
          <TextField
            label="Sender name"
            value={draft.mailFromName}
            onChange={(value) => set({ mailFromName: value })}
            placeholder="NHC IT Enrolment"
            maxLength={80}
            hint="Shown in front of the address in somebody's inbox."
          />
          <TextField
            label="Reply-to"
            type="email"
            value={draft.mailReplyTo}
            onChange={(value) => set({ mailReplyTo: value })}
            placeholder="it@newhorizoncopper.com"
            maxLength={200}
            hint="Where a reply should land. Leave empty and replies go back to the sending mailbox, which nobody reads."
          />
          <TextField
            label="Onsite tech mailbox"
            type="email"
            value={draft.onsiteTechEmail}
            onChange={(value) => set({ onsiteTechEmail: value })}
            placeholder="nhc-it@microtechnamibia.com"
            maxLength={200}
            hint="Where Push to onsite tech sends a request. Leave empty for nhc-it@microtechnamibia.com."
          />
        </Section>

        <Section
          title="Reminders"
          description="Anybody waiting on a request can send a reminder to whoever it is sitting with."
        >
          <TextField
            label="Hours between reminders on one request"
            value={draft.nudgeIntervalHours}
            onChange={(value) => set({ nudgeIntervalHours: value })}
            error={hoursError}
            hint="The limit is per request, not per person, so a busy queue cannot turn into a mailstorm from several people at once."
          />
        </Section>

        <Section title="Signed PDF" description="A running header and footer on every page of the signed record.">
          {!environment.pdfAvailable && (
            <Alert tone="warning">
              No Chromium was found on the server, so PDFs cannot be rendered at the moment. The record is
              still served as a web page, and these settings will apply once it is installed.
            </Alert>
          )}
          <TextField
            label="Header line"
            value={draft.pdfHeaderText}
            onChange={(value) => set({ pdfHeaderText: value })}
            placeholder="New Horizon Copper: IT Enrolment"
            maxLength={120}
          />
          <TextField
            label="Footer line"
            value={draft.pdfFooterText}
            onChange={(value) => set({ pdfFooterText: value })}
            placeholder="Confidential: retain per IT policy"
            maxLength={120}
            hint="Page numbers are always printed beside this, so a printed record can be checked for completeness."
          />
        </Section>

        <Section
          title="Retention"
          description="How long a completed record should be kept, as company policy."
        >
          <TextField
            label="Months to keep a completed record"
            value={draft.retentionMonths}
            onChange={(value) => set({ retentionMonths: value })}
            error={monthsError}
            placeholder="Leave empty to keep indefinitely"
            hint="Recorded and shown here. Nothing is deleted automatically: a signed record is the answer to “what did we give that person?” years later, and a job that quietly removed them would be worse than not having one. Deleting on this schedule is a deliberate step, taken once NHC has agreed the policy."
          />
        </Section>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={() => save.mutate(draft)} disabled={!dirty || blocked || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(toDraft(settings))} disabled={save.isPending}>
            Discard
          </Button>
        )}
        {!dirty && <span className="text-sm text-slate-500 dark:text-slate-400">No unsaved changes.</span>}
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-6">
      <h2 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h2>
      <p className="mt-1 mb-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{description}</p>
      <div className="grid max-w-2xl gap-5">{children}</div>
    </Card>
  );
}

/** Something the server's configuration owns. Shown, never editable. */
function Fixed({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{note}</p>
      {/* Dashed, because it is a value rather than a control. */}
      <div className="mt-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
        {value}
      </div>
    </div>
  );
}
